import { LoadScript } from '../types/LoadScript';

/**
 * Manages a script to be loaded.
 * Creates a 'script' DOM element on the document's head for it and attach event listeners on load and error.
 */

 export class ScriptLoader {

    private _scriptElement: HTMLScriptElement | null;

    _scriptInfo: LoadScript;

    public get scriptElement(): HTMLScriptElement | null {
        return this._scriptElement;
    }
    public set scriptElement(v: HTMLScriptElement | null) {
        this._scriptElement = v;
    }

    constructor(scriptInfo: LoadScript) {
        this._scriptInfo = scriptInfo;
        this._scriptElement = null
        this.load = this.load.bind(this);
    }

    /**
     * Removes the current script element from the head of the document.
     */
    _removeScriptElement() {
        if (this._scriptElement)
            document.head.removeChild(this._scriptElement);
    }

    /**
     * Adds the current script element to the head of the document.
     * @param {Function} onLoad Callback function when the script was correctly loaded.
     * @param {Function} onError Callback function when the script failed to be loaded.
     */
    _addScriptElement(onLoad: () => void, onError: () => void, useTimespan: boolean) {
        this._scriptElement = this._createScriptElement(
            this._scriptInfo.url,
            this._scriptInfo.type,
            this._scriptInfo.nomodule,
            onLoad,
            onError,
            useTimespan
        );
        document.head.appendChild(this._scriptElement);
    }

    /**
     * Creates a script element.
     * @param {*} url URL to be set on the 'src' attribute
     * @param {*} type Type of script to be set on the 'type' attribute
     * @param {*} nomodule If this script is a module or not, to be set on the 'nomodule' attribute
     * @param {*} onLoad Event listener triggered when the script was correctly loaded
     * @param {*} onError Event listener triggered when the script failed to be loaded
     * @returns {HTMLScriptElement} The script element
     */
    _createScriptElement(url: string, type: string, nomodule: boolean, onLoad: () => void, onError: () => void, includeTimestamp: boolean) {
        var script = document.createElement('script');
        script.src = (includeTimestamp)
            ? `${url}?timestamp=${new Date().getTime()}`
            : url;
        script.type = type;
        script.noModule = nomodule;
        script.async = true;
        script.onload = onLoad;
        script.onerror = onError;
        script.addEventListener('load', onLoad);
        script.addEventListener('error', onError);
        return script;
    }

    /**
     * Loads the script to the page.
     * If the script element already exists, remove it first, then add a new script element for the script.
     * @param {Function} onLoad Callback function when the script was correctly loaded.
     * @param {Function} onError Callback function when the script failed to be loaded.
     * @returns Promise of loading the script.
     */
    load(onLoad: () => void, onError: () => void, useTimespan: boolean) {
        this._removeScriptElement();
        this._addScriptElement(onLoad, onError, useTimespan);
    }
}